import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';
import { SelectBoxWithSearch, TextInput } from '../../components/Form/Form';
import Spinner from '../../components/layout/Spinner';
import BreadCrumb from '../../components/template/BreadCrumb';
import Header from '../../components/template/Header';
import api from '../../domain/api';
import { LINK_URL, PAGE_TITLE } from '../../shared/enums/clients_enum';
import { useGetDropdownOptions } from '../../shared/hooks/UseActivitystatus';
import EmbeddedClientDashboard from './_components/EmbeddedClientDashboard';

const DUE_TIME_FORMAT = (date = new Date()) => {
  const pad = (n) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ClientView = ({ match }) => {
  const [dropdownOptions] = useGetDropdownOptions();
  const queryClient = useQueryClient();

  // Fetch client data
  const { data: client, isLoading: client_loading } = useQuery({
    queryKey: ['client', match.params.id],
    queryFn: async () => {
      const resp = await api.get(`/clients/${match.params.id}`);
      return resp.data;
    },
  });

  // Add activity mutation
  const activityMutation = useMutation({
    mutationFn: async (data) => {
      const activityData = {
        ...data,
        client: match.params.id,
      };
      const resp = await api.post('/activitys', activityData);
      return resp.data;
    },
    onSuccess: () => {
      toast.success('Activity added successfully');
      // Invalidate activities query to refresh the list
      queryClient.invalidateQueries(['clientActivities', match.params.id]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add activity');
    },
  });

  if (client_loading) {
    return (
      <div className='pace-done'>
        <div>
          <Header />
          <BreadCrumb
            title='Client View'
            mainLinkTitle={PAGE_TITLE}
            mainLinkUrl={LINK_URL}
            activeLink='Client View'
          />
        </div>
        <div className='text-center'>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className='pace-done'>
      <div>
        <Header />
        <BreadCrumb
          title={`${client?.name || 'Client'} - Client View`}
          mainLinkTitle={PAGE_TITLE}
          mainLinkUrl={LINK_URL}
          activeLink='Client View'
        />
      </div>

      <div className='container-fluid'>
        <div className='row'>
          {/* Column 1: Client Details */}
          <div className='col-lg-4'>
            <div className='card'>
              <div className='card-header'>
                <h4 className='card-title'>Client Details</h4>
              </div>
              <div className='card-body'>
                {client && (
                  <div className='client-details'>
                    <div className='detail-item'>
                      <strong>Name:</strong>
                      <span>{client.name || 'N/A'}</span>
                    </div>
                    <div className='detail-item'>
                      <strong>Email:</strong>
                      <span>{client.email || 'N/A'}</span>
                    </div>
                    <div className='detail-item'>
                      <strong>Phone:</strong>
                      <span>{client.phone || 'N/A'}</span>
                    </div>
                    <div className='detail-item'>
                      <strong>Company:</strong>
                      <span>{client.company || 'N/A'}</span>
                    </div>
                    <div className='detail-item'>
                      <strong>Status:</strong>
                      <span>
                        {client.status && client.status.name
                          ? client.status.name
                          : 'N/A'}
                      </span>
                    </div>
                    <div className='detail-item'>
                      <strong>Created:</strong>
                      <span>
                        {client.createdAt
                          ? new Date(client.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Column 2: Create Activity Form */}
          <div className='col-lg-4'>
            <div className='card'>
              <div className='card-header'>
                <h4 className='card-title'>Create Activity</h4>
              </div>
              <div className='card-body'>
                <Formik
                  initialValues={{
                    task_name: '',
                    assigned_to: '',
                    due_time: '',
                    process: '',
                    actual_time: '',
                  }}
                  validationSchema={Yup.object({
                    task_name: Yup.string().required('Required'),
                    assigned_to: Yup.string().required('Required'),
                    due_time: Yup.string().required('Required'),
                    process: Yup.string().required('Required'),
                  })}
                  onSubmit={async (values, { setSubmitting, resetForm }) => {
                    try {
                      setSubmitting(true);
                      await activityMutation.mutateAsync(values);
                      resetForm();
                    } catch (error) {
                      console.error('Error submitting form:', error);
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {(formik) => (
                    <Form>
                      <div className='form-group mb-3'>
                        <TextInput
                          label='Task Name'
                          name='task_name'
                          type='text'
                          placeholder='Enter task name'
                        />
                      </div>

                      <div className='form-group mb-3'>
                        <SelectBoxWithSearch
                          label='Assigned to'
                          name='assigned_to'
                          options={
                            dropdownOptions?.assigned_to?.map((item) => ({
                              value: item.value,
                              label: item.label,
                            })) || []
                          }
                        />
                      </div>

                      <div className='form-group mb-3'>
                        <div style={{ position: 'relative' }}>
                          <TextInput
                            label='Due Time'
                            name='due_time'
                            type='datetime-local'
                            placeholder='Select due date and time'
                          />
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              marginTop: '5px',
                            }}
                          >
                            <button
                              type='button'
                              className='btn btn-xs btn-secondary'
                              onClick={() => {
                                formik.setFieldValue(
                                  'due_time',
                                  DUE_TIME_FORMAT()
                                );
                              }}
                              title='Set to now'
                            >
                              Set to Now
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className='form-group mb-3'>
                        <SelectBoxWithSearch
                          label='Process'
                          name='process'
                          placeholder='Select process'
                          options={
                            dropdownOptions?.process?.map((item) => ({
                              value: item.value,
                              label: item.label,
                            })) || []
                          }
                        />
                      </div>

                      <div className='form-group mb-3'>
                        <TextInput
                          label='Actual Time (Optional)'
                          name='actual_time'
                          type='text'
                          placeholder='Enter actual time'
                        />
                      </div>

                      <div className='form-actions'>
                        <button
                          type='submit'
                          className='btn btn-primary'
                          disabled={formik.isSubmitting}
                        >
                          {formik.isSubmitting
                            ? 'Creating...'
                            : 'Create Activity'}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>

          {/* Column 3: Checklist */}
          <div className='col-lg-4'>
            <div className='card'>
              <div className='card-header'>
                <h4 className='card-title'>Client Checklist</h4>
              </div>
              <div
                className='card-body'
                style={{ maxHeight: '600px', overflowY: 'auto' }}
              >
                {client && (
                  <>
                    {/* Data Checklist */}
                    <div className='checklist-section mb-4'>
                      <div className='p-2 mb-2 bg-light'>
                        <p className='font-weight-bold mb-0'>Data Checklist</p>
                      </div>
                      <div className='row'>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.contract_available || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>Contract</label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.fb_page_access || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              FB Page Access
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.gmb_access || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              GMB Access
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={
                                client.link_own_card_for_marketing_budget ||
                                false
                              }
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Link Own Card For Marketing Budget
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Website Checklist */}
                    <div className='checklist-section mb-4'>
                      <div className='p-2 mb-2 bg-light'>
                        <p className='font-weight-bold mb-0'>
                          Website Checklist
                        </p>
                      </div>
                      <div className='row'>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.website || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>Website</label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.android_app || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Android App
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.ios_app || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>IOS App</label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.landing_pages || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Landing Pages
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ads Checklist */}
                    <div className='checklist-section mb-4'>
                      <div className='p-2 mb-2 bg-light'>
                        <p className='font-weight-bold mb-0'>Ads Checklist</p>
                      </div>
                      <div className='row'>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.fb_ads || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Facebook Ads
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.google_ads || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Google Ads
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.instagram_ads || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Instagram Ads
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.youtube_ads || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Youtube Ads
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Social Media Posting */}
                    <div className='checklist-section mb-4'>
                      <div className='p-2 mb-2 bg-light'>
                        <p className='font-weight-bold mb-0'>
                          Social Media Posting
                        </p>
                      </div>
                      <div className='row'>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.fb_posting || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Facebook Posting
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.insta_posting || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Instagram Posting
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.linkedin_posting || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              LinkedIn Posting
                            </label>
                          </div>
                        </div>
                        <div className='col-md-12 mb-2'>
                          <div className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={client.youtube_posting || false}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label'>
                              Youtube Posting
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reporting Section */}

      {/* Client Dashboard Section (Simplified) */}
      <div className='row mt-4'>
        <div className='col-12'>
          <div className='card simple-dashboard-card'>
            <div className='card-header py-2'>
              <h5 className='card-title mb-0'>Client Dashboard & Analytics</h5>
            </div>
            <div className='card-body p-0'>
              <EmbeddedClientDashboard clientId={match.params.id} />
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .client-details .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f1f1f1;
        }

        .client-details .detail-item:last-child {
          border-bottom: none;
        }

        .client-details .detail-item strong {
          color: #374151;
          font-weight: 600;
          min-width: 80px;
        }

        .client-details .detail-item span {
          color: #6b7280;
          text-align: right;
          flex: 1;
          margin-left: 10px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          font-weight: 600;
          color: #374151;
          margin-bottom: 5px;
          display: block;
          font-size: 14px;
        }

        .form-control {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .form-control:focus {
          outline: none;
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }

        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: #dc2626;
          color: white;
        }

        .btn-primary:hover {
          background: #b91c1c;
        }

        .btn-secondary {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        }

        .btn-secondary:hover {
          background: #e5e7eb;
        }

        .btn-xs {
          padding: 2px 8px;
          font-size: 12px;
        }

        .checklist-section {
          border: 1px solid #f1f1f1;
          border-radius: 6px;
          padding: 10px;
          background: #fafafa;
        }

        .checklist-section .bg-light {
          background: #e5e7eb !important;
          border-radius: 4px;
        }

        .form-check {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .form-check-input {
          margin: 0;
          cursor: not-allowed;
        }

        .form-check-label {
          margin: 0;
          font-size: 14px;
          color: #374151;
          cursor: not-allowed;
        }

        .form-check-input:disabled {
          opacity: 0.6;
        }

        .nav-tabs .nav-link {
          border: 1px solid transparent;
          border-radius: 4px 4px 0 0;
          color: #6c757d;
          font-weight: 500;
          padding: 10px 20px;
          cursor: pointer;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          transition: all 0.2s ease;
        }

        .nav-tabs .nav-link:hover {
          color: #dc2626;
          border-bottom-color: #dc2626;
        }

        .nav-tabs .nav-link.active {
          color: #dc2626;
          border-bottom-color: #dc2626;
          background: none;
        }

        .status-card {
          border-radius: 10px;
          transition: transform 0.2s ease;
        }

        .status-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .status-completed {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .status-pending {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
        }

        .status-onhold {
          background: linear-gradient(135deg, #6b7280, #4b5563);
          color: white;
        }

        .status-overdue {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
        }

        .status-default {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
        }

        .status-icon {
          opacity: 0.8;
        }

        .status-count {
          font-weight: 700;
          font-size: 2rem;
        }

        .status-label {
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.75rem;
        }

        .activities-tab .table {
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }

        .activities-tab .table thead th {
          background: #f8f9fa;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
          padding: 12px;
        }

        .activities-tab .table tbody td {
          padding: 12px;
          border-bottom: 1px solid #f1f1f1;
        }

        .activities-tab .table tbody tr:hover {
          background: #f8f9fa;
        }

        .pagination .page-link {
          border: 1px solid #e5e7eb;
          color: #6b7280;
          padding: 8px 12px;
          margin: 0 2px;
          border-radius: 6px;
        }

        .pagination .page-item.active .page-link {
          background: #dc2626;
          border-color: #dc2626;
          color: white;
        }

        .pagination .page-link:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .pagination .page-item.disabled .page-link {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Simplified dashboard card styles */
        .simple-dashboard-card {
          border: 1px solid #e5e7eb;
          box-shadow: none;
          background: #fff;
        }
        .simple-dashboard-card .card-header {
          background: #fff;
          border-bottom: 1px solid #f1f1f1;
        }
        .simple-dashboard-card .card-body {
          background: #fff;
        }
      `}</style>
    </div>
  );
};

export default ClientView;
